import type { ReactNode } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { css } from '@compiled/react';

import Tabs, { Tab, TabList, useTabPanel } from '@atlaskit/tabs';
import { Text, Inline, Box } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';

import { useAccessRequests } from '@confluence/access-requests/entry-points/useAccessRequests.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../../controller/Controller.tsx';

import type { Principal, PrincipalAndRole, Role } from '../../../commonTypes.tsx';
import { ConfluenceGroupUsageType } from '../../../../controller/queries/__types__/useQueriesShareAndRestrictDialogQuery.ts';
import {
	AccessRequestsDialogBody,
	type AccessRequestsDialogBodyProps,
} from '../accessRequests/AccessRequestsDialogBody.tsx';
import type { SpecificAccessDetailsProps } from '../specificAccessDetails/SpecificAccessDetails.tsx';

import { SpecificAccessPeopleTab } from './SpecificAccessPeopleTab.tsx';
import {
	applyMutationPayloadToLiveDelta,
	createEmptySpecificAccessLiveDelta,
} from './specificAccessDialogLiveState.ts';
import { useLiveSpecificAccessDerivedState } from './liveSpecificAccessDerivedState.ts';
import { useLiveSpecificAccessActions } from './specificAccessDialogHooks.ts';
import { useUpdateContentDirectRestrictions } from '../../../../controller/mutations/useUpdateContentDirectRestrictions.ts';
import { useSpecificAccessFlags } from './useSpecificAccessFlags.tsx';
import { useSpecificAccessLiveMutation } from './useSpecificAccessLiveMutation.ts';
import type {
	SpecificAccessLiveMutationArgs,
	SpecificAccessDialogContext,
} from './specificAccessDialogTypes.ts';
import type { useUpdateContentDirectRestrictionsMutation$data as MutationData } from '../../../../controller/mutations/__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';
import { NewAccessRequestIndicator } from '@confluence/access-requests/entry-points/NewAccessRequestIndicator.tsx';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	// Add extra spacing so the NewAccessRequestIndicator notification dot has room to render.
	// Without it, the tab text becomes misaligned, so we apply the same spacing to both tabs
	// (even when the dot isn’t shown) to keep the labels aligned.
	spacing: {
		padding: token('space.025'),
	},
});

type PrincipalRow = NonNullable<NonNullable<SpecificAccessDetailsProps['principals']>[number]>;
type EffectivePrincipalsMap = Map<string, { principal: Principal; role: Role }>;

/**
 * Builds the live-mode principals list by:
 * 1. Filtering out principals no longer in the effective set
 * 2. Deriving effective roles from the live delta
 * 3. Wiring mutation callbacks for role changes and removals
 */
function buildLivePrincipals({
	principals,
	effectivePrincipalsMap,
	savingPrincipalIds,
	onChangeRole,
	onRemovePrincipal,
}: {
	principals: SpecificAccessDetailsProps['principals'];
	effectivePrincipalsMap: EffectivePrincipalsMap;
	savingPrincipalIds: Set<string>;
	onChangeRole: (principalId: string, nextRole: Role, previousRole: Role) => Promise<void>;
	onRemovePrincipal: (principalId: string, previousRole: Role) => Promise<void>;
}): PrincipalRow[] {
	const effectiveIds = new Set(effectivePrincipalsMap.keys());
	const result: PrincipalRow[] = [];

	for (const row of principals ?? []) {
		if (!row) {
			continue;
		}

		const principalId = row.principalDetails.principal.id;
		if (!effectiveIds.has(principalId)) {
			continue;
		}

		const effectiveEntry = effectivePrincipalsMap.get(principalId);
		const effectiveRole = effectiveEntry?.role ?? row.principalDetails.role;

		result.push({
			...row,
			principalDetails: {
				...row.principalDetails,
				role: effectiveRole,
			},
			principalAccess: {
				...row.principalAccess,
				access: effectiveRole,
				isSaving: savingPrincipalIds.has(principalId),
				onChange: async (role: Role) => onChangeRole(principalId, role, row.principalDetails.role),
				onRemove: async () => onRemovePrincipal(principalId, effectiveRole),
			},
		});
	}

	return result;
}

const i18n = defineMessages({
	peopleWithAccessLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog-body.people-with-access',
		defaultMessage: 'People with access',
		description: 'Label for tab for people with access',
	},
	requestsLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog-body.requests',
		defaultMessage: 'Requests',
		description: 'Tab label showing pending access requests for content in the sharing dialog',
	},
});

export type SpecificAccessDialogBodyProps = {
	contentId: string;
	principalCounts: {
		countOfIndividuals: number;
		countOfTeams: number;
		countOfGroups: number;
	};
	specificAccessDetails?: SpecificAccessDetailsProps;
	accessRequestsDialogBody?: AccessRequestsDialogBodyProps;

	/**
	 * Explicit persistence mode so this module can run in live vs staged contexts.
	 */
	persistenceMode: 'live' | 'staged';

	/**
	 * Live-mode integration for the Specific Access dialog.
	 */
	liveMutationContext: {
		currentUser: Principal;
		/**
		 * Used for computing whether live mutations should include invites.
		 * TODO: includeInvites support is currently not implemented for the live dialog yet.
		 */
		principalsPendingAccess: PrincipalAndRole[];
	};

	/**
	 * Staged-mode integration for the Specific Access dialog (publish modal flow).
	 */
	stagedActionContext?: {
		removeAllSpecificAccessPrincipals: () => void;
	};

	/**
	 * Context for background refetch.
	 * Used to refresh the People tab after approving access requests.
	 */
	specificAccessDialogContext?: SpecificAccessDialogContext;

	/**
	 * Initial tab to show when the dialog opens.
	 * 0 = People tab (default), 1 = Requests tab
	 */
	initialTab?: number;
};

const customPanelStyles = css({
	alignItems: 'stretch',
	width: '100%',
});

const tabPanelContentStyles = css({
	flex: '1 1 auto',
	width: '100%',
	minWidth: 0,
});

type CustomTabPanelProps = { children: ReactNode };

const CustomTabPanel = ({ children }: CustomTabPanelProps) => {
	const tabPanelAttributes = useTabPanel();

	return (
		<div css={customPanelStyles} {...tabPanelAttributes}>
			<div css={tabPanelContentStyles}>{children}</div>
		</div>
	);
};

const SpecificAccessDialogBodyInternal = ({
	contentId,
	principalCounts,
	specificAccessDetails,
	accessRequestsDialogBody,
	persistenceMode,
	liveMutationContext,
	stagedActionContext,
	specificAccessDialogContext,
	initialTab = 0,
	flags,
}: SpecificAccessDialogBodyProps & WithFlagsProps) => {
	const [tabSelection, setTabSelection] = useState(initialTab);
	const [hasApprovedRequests, setHasApprovedRequests] = useState(false);
	const [processedRequestsCount, setProcessedRequestsCount] = useState(0);
	const previousTabRef = useRef<number>(initialTab);
	const { requests } = useAccessRequests('cache-and-network', 'SpecificAccessDialogBody');

	const count = requests?.length ?? 0;
	// Compute effective count by subtracting locally processed requests
	// This ensures the badge updates immediately when requests are approved/denied
	const effectiveCount = Math.max(0, count - processedRequestsCount);

	const { currentUser, principalsPendingAccess } = liveMutationContext;

	const { cloudId } = useSessionData();
	const [mutateDirectRestrictions] = useUpdateContentDirectRestrictions();

	// --- Live delta state (dialog-owned) ---
	const [liveSpecificAccessDelta, setLiveSpecificAccessDelta] = useState(
		createEmptySpecificAccessLiveDelta,
	);
	const [savingPrincipalIds, setSavingPrincipalIds] = useState<Set<string>>(() => new Set());

	const { effectivePrincipalsMap, specificAccessPrincipalById } = useLiveSpecificAccessDerivedState(
		{
			specificAccessDetails,
			currentUserId: currentUser.id,
			liveSpecificAccessDelta,
			principalsPendingAccess,
		},
	);

	const specificAccessFlags = useSpecificAccessFlags({ flags });

	const principalById = useMemo(() => {
		const map = new Map<string, { principal: Principal }>();
		for (const [id, entry] of specificAccessPrincipalById.entries()) {
			map.set(id, { principal: entry.principal });
		}
		return map;
	}, [specificAccessPrincipalById]);

	/**
	 * Reconciles the dialog's live delta state with the mutation response so UI
	 * updates immediately without a full refetch.
	 */
	const handleMutationApplied = useCallback(
		({
			payload,
			mutationArgs,
		}: {
			payload: NonNullable<MutationData['confluence_updateContentDirectRestrictions']>;
			mutationArgs: SpecificAccessLiveMutationArgs;
		}) => {
			// Keep the dialog's local delta aligned with the server-applied restrictions.
			setLiveSpecificAccessDelta((prev) =>
				applyMutationPayloadToLiveDelta({
					liveDelta: prev,
					payload,
					mutationArgs,
				}),
			);
		},
		[],
	);

	const { triggerLiveMutation: triggerLiveMutationInternal } = useSpecificAccessLiveMutation({
		cloudId,
		contentId,
		// this mutation wil not include invites by default
		// because its not apart of the sharing flow
		includeInvites: false,
		principalById,
		mutateDirectRestrictions,
		flags: specificAccessFlags,
		onMutationApplied: handleMutationApplied,
	});

	const triggerLiveMutation = useCallback(
		async (args: SpecificAccessLiveMutationArgs) => {
			if (persistenceMode === 'staged') {
				return;
			}
			await triggerLiveMutationInternal(args);
		},
		[persistenceMode, triggerLiveMutationInternal],
	);

	const { onChangeRole, onRemovePrincipal, onRemoveAllPrincipals } = useLiveSpecificAccessActions({
		setLiveSpecificAccessDelta,
		setSavingPrincipalIds,
		triggerLiveMutation,
	});

	// Callback for when an access request is processed
	// Updates the badge count immediately and tracks approvals for background refetch
	const handleRequestProcessed = useCallback((decision: 'approved' | 'denied') => {
		setProcessedRequestsCount((prev) => prev + 1);
		if (decision === 'approved') {
			setHasApprovedRequests(true);
		}
	}, []);

	const handleTabChange = useCallback(
		async (newTab: number) => {
			const previousTab = previousTabRef.current;
			previousTabRef.current = newTab;

			// Switching from Requests tab (1) to People tab (0) with pending approvals
			// Only trigger background refetch in live mode with the experiment enabled
			const shouldRefresh =
				previousTab === 1 &&
				newTab === 0 &&
				hasApprovedRequests &&
				persistenceMode === 'live' &&
				specificAccessDialogContext?.backgroundRefetch;

			if (shouldRefresh) {
				// Switch tab immediately so user sees the People tab
				setTabSelection(newTab);

				// Trigger background refetch - spinner is controlled by isBackgroundRefetching
				// which flows down from useQueries via Controller
				try {
					await specificAccessDialogContext.backgroundRefetch();
				} finally {
					setHasApprovedRequests(false);
				}
			} else {
				setTabSelection(newTab);
			}
		},
		[hasApprovedRequests, persistenceMode, specificAccessDialogContext],
	);

	const getSpecificAccessDetails = useCallback((): SpecificAccessDetailsProps | undefined => {
		if (!specificAccessDetails) {
			return undefined;
		}

		if (persistenceMode === 'staged') {
			return specificAccessDetails;
		}

		// Live mode: apply local delta updates to the list + use live mutation actions
		const livePrincipals = buildLivePrincipals({
			principals: specificAccessDetails.principals,
			effectivePrincipalsMap,
			savingPrincipalIds,
			onChangeRole,
			onRemovePrincipal,
		});

		return {
			...specificAccessDetails,
			principals: livePrincipals,
		};
	}, [
		effectivePrincipalsMap,
		onChangeRole,
		onRemovePrincipal,
		persistenceMode,
		savingPrincipalIds,
		specificAccessDetails,
	]);

	/**
	 * Derived counts for the live dialog, reflecting removals/changes in the local delta.
	 */
	const effectivePrincipalCounts = useMemo(() => {
		if (persistenceMode === 'staged') {
			return principalCounts;
		}

		let effectiveIndividuals = 0;
		let effectiveTeams = 0;
		let effectiveGroups = 0;

		for (const { principal } of effectivePrincipalsMap.values()) {
			if (principal.type === 'user') {
				effectiveIndividuals += 1;
				continue;
			}

			if (
				principal.type === 'team' ||
				(principal.type === 'group' &&
					principal.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION)
			) {
				effectiveTeams += 1;
				continue;
			}

			if (principal.type === 'group') {
				effectiveGroups += 1;
			}
		}

		return {
			countOfIndividuals: effectiveIndividuals,
			countOfTeams: effectiveTeams,
			countOfGroups: effectiveGroups,
		};
	}, [effectivePrincipalsMap, persistenceMode, principalCounts]);

	const onRemoveAll = useCallback(async (): Promise<void> => {
		if (persistenceMode === 'staged' && stagedActionContext) {
			stagedActionContext.removeAllSpecificAccessPrincipals();
		} else {
			// Build a map of principal IDs to their roles for the bulk remove mutation
			const principalRoles = new Map<string, Role>();
			for (const [id, entry] of specificAccessPrincipalById.entries()) {
				principalRoles.set(id, entry.role);
			}

			await onRemoveAllPrincipals({
				currentUserId: currentUser.id,
				principalIds: Array.from(specificAccessPrincipalById.keys()),
				principalRoles,
			});
		}
	}, [
		currentUser.id,
		onRemoveAllPrincipals,
		persistenceMode,
		specificAccessPrincipalById,
		stagedActionContext,
	]);

	return (
		<Tabs onChange={handleTabChange} selected={tabSelection} id="controlled">
			<TabList>
				<Tab testId="specific-access-tab-people">
					<Box xcss={styles.spacing}>
						<FormattedMessage {...i18n.peopleWithAccessLabel} />
					</Box>
				</Tab>
				<Tab testId="specific-access-tab-requests">
					<NewAccessRequestIndicator
						top={-2}
						right={-2}
						isInsideOverlay
						source={SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE}
					>
						<Inline space="space.075" alignBlock="center" xcss={styles.spacing}>
							<Text>
								<FormattedMessage {...i18n.requestsLabel} />
							</Text>
							{effectiveCount ? (
								<Badge appearance="default" max={9}>
									{effectiveCount}
								</Badge>
							) : null}
						</Inline>
					</NewAccessRequestIndicator>
				</Tab>
			</TabList>
			<CustomTabPanel>
				<SpecificAccessPeopleTab
					principalCounts={effectivePrincipalCounts}
					specificAccessDetails={getSpecificAccessDetails()}
					onRemoveAll={onRemoveAll}
					isRefreshing={specificAccessDialogContext?.isBackgroundRefetching}
				/>
			</CustomTabPanel>
			<CustomTabPanel>
				{accessRequestsDialogBody && (
					<AccessRequestsDialogBody
						{...accessRequestsDialogBody}
						onRequestProcessed={handleRequestProcessed}
					/>
				)}
			</CustomTabPanel>
		</Tabs>
	);
};

export const SpecificAccessDialogBody = withFlags(SpecificAccessDialogBodyInternal);
